import cloneDeep from 'lodash/cloneDeep';
import {actions, ActionType} from './action';
import {createReducer, ReducerActionHelper} from '../../common/util/redux';
import {INITIAL_COMMENT_LIST} from '../common';
import {
  IComment,
  ICommentLikeStatus,
  ICommentList,
  ICommentSort,
} from '../type';

export interface IStateComment {
  episodeCommentList: ICommentList;
  contentCommentListMap: {[contentId: number]: ICommentList};
  eventCommentList: ICommentList;
}

const INITIAL_STATE: IStateComment = {
  episodeCommentList: INITIAL_COMMENT_LIST,
  contentCommentListMap: {},
  eventCommentList: {...INITIAL_COMMENT_LIST, sort: 'createdDt'},
};

export default createReducer<
  IStateComment,
  ReducerActionHelper<typeof actions>
>(INITIAL_STATE, {
  [ActionType.AddEpisodeComments]: ({episodeCommentList: list}, action) => {
    addCommentsToList({list, ...action.payload});
  },
  [ActionType.AddEpisodeReplyComments]: (
    {episodeCommentList: list},
    action
  ) => {
    addReplyComments({list, ...action.payload});
  },
  [ActionType.SetEpisodeReplyOpen]: ({episodeCommentList: list}, action) => {
    const {commentId, isOpen} = action.payload;
    list.sectionMap[commentId].isOpen = isOpen;
  },
  [ActionType.SetEpisodeLikeStatus]: ({episodeCommentList: list}, action) => {
    setLikeStatus({list, ...action.payload});
  },
  [ActionType.DeleteEpisodeComment]: ({episodeCommentList: list}, action) => {
    const {commentId} = action.payload;
    deleteComment(list, commentId);
  },
  [ActionType.SetEpisodeCommentSort]: ({episodeCommentList: list}, action) => {
    list.sort = action.payload.sort;
  },

  [ActionType.AddContentComments]: ({contentCommentListMap}, action) => {
    const {contentId, ...rest} = action.payload;
    const list = (contentCommentListMap[contentId] ??=
      cloneDeep(INITIAL_COMMENT_LIST));
    addCommentsToList({list, ...rest});
  },
  [ActionType.AddContentReplyComments]: ({contentCommentListMap}, action) => {
    const {contentId, ...rest} = action.payload;
    const list = contentCommentListMap[contentId];
    addReplyComments({list, ...rest});
  },
  [ActionType.SetContentReplyOpen]: ({contentCommentListMap}, action) => {
    const {contentId, commentId, isOpen} = action.payload;
    contentCommentListMap[contentId].sectionMap[commentId].isOpen = isOpen;
  },
  [ActionType.SetContentLikeStatus]: ({contentCommentListMap}, action) => {
    const {contentId, ...rest} = action.payload;
    const list = contentCommentListMap[contentId];
    setLikeStatus({list, ...rest});
  },
  [ActionType.DeleteContentComment]: ({contentCommentListMap}, action) => {
    const {contentId, commentId} = action.payload;
    const list = contentCommentListMap[contentId];
    deleteComment(list, commentId);
  },
  [ActionType.SetContentCommentSort]: ({contentCommentListMap}, action) => {
    const {contentId, sort} = action.payload;
    const list = contentCommentListMap[contentId];
    list.sort = sort;
  },

  [ActionType.AddEventComments]: ({eventCommentList: list}, action) => {
    addCommentsToList({list, ...action.payload});
  },
  [ActionType.AddEventReplyComments]: ({eventCommentList: list}, action) => {
    addReplyComments({list, ...action.payload});
  },
  [ActionType.SetEventReplyOpen]: ({eventCommentList: list}, action) => {
    const {commentId, isOpen} = action.payload;
    list.sectionMap[commentId].isOpen = isOpen;
  },
  [ActionType.SetEventLikeStatus]: ({eventCommentList: list}, action) => {
    setLikeStatus({list, ...action.payload});
  },
  [ActionType.DeleteEventComment]: ({eventCommentList: list}, action) => {
    const {commentId} = action.payload;
    deleteComment(list, commentId);
  },
  [ActionType.SetEventCommentSort]: ({eventCommentList: list}, action) => {
    list.sort = action.payload.sort;
  },
  [ActionType.SetWriterBlock]: (
    {episodeCommentList, eventCommentList, contentCommentListMap},
    action
  ) => {
    const {userId, isBlock, contentId} = action.payload;
    if (contentId) {
      setWriterBlock(contentCommentListMap[contentId], userId, isBlock);
    } else {
      setWriterBlock(episodeCommentList, userId, isBlock);
      setWriterBlock(eventCommentList, userId, isBlock);
    }
  },
});

function addCommentsToList({
  list,
  comments,
  isFirst,
  sort,
}: {
  list: ICommentList;
  comments: IComment[];
  isFirst?: boolean;
  sort: ICommentSort;
}) {
  if (isFirst) {
    list.ids = [];
    list.sectionMap = {};
    list.commentMap = {};
  }

  const newComments = comments.filter(c => !list.ids.includes(c.id));
  list.ids.push(...newComments.map(c => c.id));
  newComments.forEach(comment => {
    list.sectionMap[comment.id] = {
      id: comment.id,
      isOpen: false,
      replyIds: [],
      isEnd: false,
    };
    list.commentMap[comment.id] = comment;
  });
  list.sort = sort;
}

function addReplyComments({
  list,
  parentId,
  replyComments,
  isFirst,
  isEnd,
  replyTotalCount,
}: {
  list: ICommentList;
  parentId: number;
  replyComments: IComment[];
  isFirst?: boolean;
  isEnd: boolean;
  replyTotalCount: number;
}) {
  const section = list.sectionMap[parentId];
  if (isFirst) {
    section.replyIds.forEach(id => delete list.commentMap[id]);
    section.replyIds = [];
  }
  list.commentMap[parentId].replyCount = replyTotalCount;
  replyComments.forEach(c => (list.commentMap[c.id] = c));
  section.replyIds.push(...replyComments.map(c => c.id));
  section.isOpen = true;
  section.isEnd = isEnd;
}

function setLikeStatus({
  list,
  commentId,
  likeStatus,
}: {
  list: ICommentList;
  commentId: number;
  likeStatus: ICommentLikeStatus;
}) {
  const comment = list.commentMap[commentId];
  const prevLikeStatus = comment.likeStatus;

  if (prevLikeStatus === 'like') {
    comment.likeCount--;
  } else if (prevLikeStatus === 'dislike') {
    comment.dislikeCount--;
  }

  if (likeStatus === 'like') {
    comment.likeCount++;
  } else if (likeStatus === 'dislike') {
    comment.dislikeCount++;
  }

  comment.likeStatus = likeStatus;
}

function deleteComment(list: ICommentList, commentId: number) {
  const comment = list.commentMap[commentId];
  if (comment.isReply) {
    const section = list.sectionMap[comment.parentId];
    section.replyIds = section.replyIds.filter(rid => rid !== commentId);
    list.commentMap[comment.parentId].replyCount--;
  } else {
    list.ids = list.ids.filter(cid => cid !== commentId);
    delete list.sectionMap[commentId];
  }
  delete list.commentMap[commentId];
}

function setWriterBlock(list: ICommentList, userId: string, isBlock: boolean) {
  Object.values(list.commentMap).forEach(comment => {
    if (comment.userId === userId) {
      comment.isBlock = isBlock;
    }
  });
}
